import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { recipesApi } from '@/api/recipes';
import { useAuth }    from '@/providers/AuthProvider';
import {
  PageHeader, Button, Badge, Spinner, Card, CardHeader, CardTitle, CardContent,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { ArrowLeft, Pencil, Thermometer } from 'lucide-react';
import { formatCurrency, formatPct } from '@/lib/utils';

export default function RecipeDetailPage() {
  const { t }       = useTranslation();
  const { id }      = useParams<{ id: string }>();
  const { isAdmin } = useAuth();

  const { data: recipe, isLoading } = useQuery({
    queryKey: ['recipe', id],
    queryFn:  () => recipesApi.get(id!),
    enabled:  !!id,
  });

  if (isLoading) return <Spinner className="py-20" />;
  if (!recipe)   return <p className="py-10 text-center text-muted-foreground">Recipe not found.</p>;

  const calc = recipe.calculations;

  return (
    <div>
      <PageHeader title={recipe.name} subtitle={recipe.recipe_type_name ?? undefined}>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/recipes"><ArrowLeft size={15} /> {t('common.back')}</Link>
          </Button>
          {isAdmin && (
            <Button asChild size="sm">
              <Link to={`/recipes/${id}/edit`}><Pencil size={14} /> {t('common.edit')}</Link>
            </Button>
          )}
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column: metadata */}
        <div className="space-y-4 lg:col-span-2">
          {/* Ingredient lines */}
          <Card>
            <CardHeader><CardTitle>{t('recipes.ingredients')}</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('ingredients.name')}</TableHead>
                    <TableHead className="text-right">{t('recipes.gramsPerKilo')}</TableHead>
                    <TableHead className="text-right">{t('recipes.wasteRatio')}</TableHead>
                    <TableHead className="text-right">{t('recipes.totalGrams')}</TableHead>
                    <TableHead className="text-right">{t('recipes.costContribution')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recipe.ingredients.map((line, idx) => {
                    const lc = calc?.line_calculations.find(l => l.ingredient_id === line.ingredient_id);
                    return (
                      <TableRow key={line.id ?? idx}>
                        <TableCell className="font-medium">{line.ingredient_name}</TableCell>
                        <TableCell className="text-right font-mono">{Number(line.grams_per_kilo).toFixed(2)}</TableCell>
                        <TableCell className="text-right">{(Number(line.waste_ratio) * 100).toFixed(1)}%</TableCell>
                        <TableCell className="text-right font-mono">{Number(line.total_grams).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono">{lc ? formatCurrency(lc.cost_contribution) : '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Notes */}
          {(recipe.preparation_notes || recipe.presentation_notes || recipe.kitchen_notes) && (
            <Card>
              <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {recipe.preparation_notes && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('recipes.preparationNotes')}</p>
                    <p className="text-sm whitespace-pre-wrap">{recipe.preparation_notes}</p>
                  </div>
                )}
                {recipe.presentation_notes && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('recipes.presentationNotes')}</p>
                    <p className="text-sm whitespace-pre-wrap">{recipe.presentation_notes}</p>
                  </div>
                )}
                {recipe.kitchen_notes && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('recipes.kitchenNotes')}</p>
                    <p className="text-sm whitespace-pre-wrap">{recipe.kitchen_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column: summary */}
        <div className="space-y-4">
          {calc && (
            <>
              {/* Production */}
              <Card>
                <CardHeader><CardTitle>{t('recipes.production')}</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <Row label={t('recipes.totalProduction')} value={`${calc.total_production_grams} g`} />
                  <Row label={t('recipes.portions')}         value={String(calc.number_of_portions)} />
                  <Row label={t('recipes.costPerPortion')}   value={formatCurrency(calc.cost_per_portion)} bold />
                  <Row label={t('recipes.recommendedPrice')} value={formatCurrency(calc.recommended_price)} />
                  {recipe.selling_price != null && (
                    <>
                      <Row label={t('recipes.netPrice')}     value={formatCurrency(calc.net_selling_price)} />
                      <Row label={t('recipes.netProfit')}    value={formatCurrency(calc.net_profit)} />
                      <Row label={t('recipes.actualMargin')} value={formatPct(calc.actual_margin_pct)} />
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Serving temp */}
              <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/30">
                <CardContent className="flex items-center gap-3 p-4">
                  <Thermometer size={22} className="text-blue-500" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('recipes.servingTemp')}</p>
                    <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{calc.suggested_serving_temp}</p>
                  </div>
                  <Badge className="ml-auto">{t('recipes.totalPAC')}: {calc.total_pac}</Badge>
                </CardContent>
              </Card>

              {/* Technical balance */}
              <Card>
                <CardHeader><CardTitle>{t('recipes.technicals')}</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <Row label={t('recipes.totalSugars')}  value={formatPct(calc.total_sugars_pct)} />
                  <Row label={t('recipes.totalFat')}     value={formatPct(calc.total_fat_pct)} />
                  <Row label={t('recipes.totalSML')}     value={formatPct(calc.total_sml_pct)} />
                  <Row label="Solids (non-fat)"          value={formatPct(calc.total_solids_diff)} />
                  <Row label={t('recipes.totalSolids')}  value={formatPct(calc.total_solids_total)} />
                  <Row label={t('recipes.totalPOD')}     value={String(calc.total_pod)} />
                  <Row label={t('recipes.totalPAC')}     value={String(calc.total_pac)} bold />
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={bold ? 'font-semibold' : ''}>{value}</span>
    </div>
  );
}
